import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import socketIoConnection from '../socketIoConnect';
import { useAuth } from '../context/auth-context';

/**
 * Hook that listens for automation events and invalidates relevant query caches
 * Should be called once at the App level, inside AuthProvider. The connection is
 * gated on an authenticated session: an unauthenticated handshake is rejected by
 * the server and would reconnect indefinitely.
 */
const useAutomationEvents = () => {
  const queryClient = useQueryClient();
  const accessToken = useAuth()?.accessToken;

  useEffect(() => {
    if (!accessToken) return undefined;

    // Connect to bunmail socket namespace (where automation events are broadcast)
    const socket = socketIoConnection('bunmail');
    if (!socket) return undefined;

    const handleAutomationEvent = (event) => {
      console.log('[AUTOMATION-EVENT] Received:', event);

      switch (event.type) {
        case 'reminder_created':
          // Invalidate reminder queries
          console.log('[AUTOMATION-EVENT] Invalidating reminder queries for household:', event.household_id);
          queryClient.invalidateQueries(['upcoming-reminders']);
          queryClient.invalidateQueries(['reminders']);
          if (event.household_id) {
            queryClient.invalidateQueries(['upcoming-reminders', event.household_id]);
            queryClient.invalidateQueries(['reminders', event.household_id]);
          }
          break;

        case 'email_draft_created':
          // Drafts now live in DraftContext (localStorage) — the draft:created
          // socket event is handled directly by DraftContext, so no query
          // invalidation needed here.
          break;

        case 'note_created':
          // Invalidate note queries
          console.log('[AUTOMATION-EVENT] Invalidating note queries for household:', event.household_id);
          queryClient.invalidateQueries(['household-notes']);
          if (event.household_id) {
            queryClient.invalidateQueries(['household-notes', event.household_id]);
          }
          break;

        default:
          console.log('[AUTOMATION-EVENT] Unknown event type:', event.type);
      }
    };

    socket.on('automation:event', handleAutomationEvent);

    return () => {
      socket.off('automation:event', handleAutomationEvent);
      socket.disconnect();
    };
  }, [queryClient, accessToken]);
};

export default useAutomationEvents;





